import { AssetType, type Asset, type CoverStrategies } from '@seek/brand-types';
import { uploadLogo } from '@seek/branding-assets';
import setDebug from 'debug';
import { compact } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import pick from 'lodash/pick';
import set from 'lodash/set';

import type { Brand } from 'src/App/generated/graphql-types';
import { getToken } from 'src/modules/authHandler';
import {
  uploadNewCover,
  updateExistingCover,
} from 'src/modules/imageServiceUploader';
import trackingClient from 'src/modules/impressionTracker';
import { useStore } from 'src/state';
import type { Toast, SelectedLogo } from 'src/types';
import { isProd } from 'src/utils/environment';
import checkImageReadiness from 'src/utils/image-readiness-checker';

import translations from './.vocab';
import {
  brandStateVar,
  selectedFileVar,
  isCoverSavingVar,
  isLogoSavingVar,
  toastQueueVar,
  originalBrandStateVar,
} from './cache';

const debug = setDebug('StateActions');
const validBrandAssetProps = ['id', 'url', 'strategies'];

const getMessageByKey = (language: string, key: string): string => {
  // @ts-ignore
  const messages = translations.getLoadedMessages(language);
  // @ts-ignore
  return messages ? messages[key].format() : 'Error';
};

// =================================================================
// Modal actions
// =================================================================

export const openAssetLibraryModal = (assetType: AssetType) => {
  debug('opening asset library modal ', assetType);
  const isLogoModalOpen = assetType === AssetType.LOGO;
  const isCoverModalOpen = assetType === AssetType.COVER;
  useStore.setState({ isLogoModalOpen });
  useStore.setState({ isCoverModalOpen });
};

export const closeAssetLibraryModal = () => {
  debug('closing asset library modal');
  useStore.setState({ isLogoModalOpen: false });
  useStore.setState({ isCoverModalOpen: false });
};

export const openCoverCropperModal = () => {
  debug('opening cover cropper modal');
  useStore.setState({ isCoverCropperModalOpen: true });
};

export const closeCoverCropperModal = () => {
  debug('closing cover cropper modal');
  useStore.setState({ isCoverCropperModalOpen: false });
};

// =================================================================
// Asset actions - logos and covers
// =================================================================

const processError = ({
  errorMessage,
  language,
  showToast,
  loadingToggle,
}: {
  errorMessage?: string;
  language: string;
  showToast: (toast: Toast) => void;
  loadingToggle: (state: boolean) => void;
}) => {
  const description = getMessageByKey(
    language,
    errorMessage
      ? errorMessage
      : 'Something went wrong! Please try again or contact customer support.',
  );

  removeCover();
  showToast({
    tone: 'critical',
    message: getMessageByKey(language, `Oops!`),
    description,
  });
  loadingToggle(false);
  return;
};

export const selectFile = (file: File) => {
  debug('selecting a file', file);
  selectedFileVar(file);
};

export const selectLogo = async (
  logo: SelectedLogo,
  language: string,
  showToast: (toast: Toast) => void,
) => {
  const isExistingLogo = 'id' in logo; // { id, strategies, url }
  const isNewLogo = 'file' in logo; // { file, url }

  if (!isNewLogo && !isExistingLogo) {
    return processError({
      errorMessage: 'Invalid LogoInput',
      language,
      showToast,
      loadingToggle: isLogoSavingVar,
    });
  }

  let nextLogo = null;
  if (isExistingLogo) {
    nextLogo = logo;
  }

  if (isNewLogo) {
    isLogoSavingVar(true);

    const response = await uploadLogo({
      image: logo.file,
      getToken,
      isProd: isProd(),
    }).catch(() =>
      processError({
        language,
        showToast,
        loadingToggle: isLogoSavingVar,
      }),
    );

    if (!response) {
      return;
    }
    nextLogo = response;
  }

  // TODO: Both of the selectLogo and selectCover have significant amount
  // of duplication, this could be refactored and simplified.

  // @ts-ignore
  const jdpLogoLink = nextLogo && nextLogo?.strategies?.jdpLogo;

  if (!nextLogo || !jdpLogoLink) {
    return;
  }

  const jdpLogoUrl = new URL(jdpLogoLink);
  const [imageId, childId] = compact(jdpLogoUrl?.pathname?.split('/'));

  if (!imageId || !childId) {
    return processError({
      errorMessage: 'Brand is corrupted',
      language,
      showToast,
      loadingToggle: isLogoSavingVar,
    });
  }

  const readinessState = await checkImageReadiness({
    id: imageId,
    childId,
  });

  if (readinessState?.error) {
    return processError({
      errorMessage: readinessState?.error,
      language,
      showToast,
      loadingToggle: isLogoSavingVar,
    });
  }

  const nextBrandState = set(
    cloneDeep(brandStateVar()),
    'assets.logo',
    pick(nextLogo, validBrandAssetProps),
  );
  brandStateVar(nextBrandState);
  isLogoSavingVar(false);
};

export const selectCover = async (
  { cover }: { cover: any },
  language: string,
  showToast: (toast: Toast) => void,
) => {
  debug('selecting cover', cover);
  isCoverSavingVar(true);

  const { file, id, croppedCoverInfo: croppedCoverInfo } = cover || {};
  const croppedInfo = croppedCoverInfo?.data;

  if ((!file && !id) || !croppedInfo) {
    return processError({
      errorMessage: 'Invalid CoverInput',
      language,
      showToast,
      loadingToggle: isCoverSavingVar,
    });
  }

  if (file?.type === 'image/svg+xml') {
    return processError({
      errorMessage: 'Cover image does not support SVG.',
      language,
      showToast,
      loadingToggle: isCoverSavingVar,
    });
  }

  const isNewCover = Boolean(file);

  const res = isNewCover
    ? await uploadNewCover({
        file,
        croppedInfo,
      }).catch(() =>
        processError({
          language,
          showToast,
          loadingToggle: isCoverSavingVar,
        }),
      )
    : await updateExistingCover({
        id,
        croppedInfo,
      }).catch(() =>
        processError({
          language,
          showToast,
          loadingToggle: isCoverSavingVar,
        }),
      );

  const jdpCoverLink = res && res?.strategies?.jdpCover;

  if (!res || !jdpCoverLink) {
    return;
  }

  const jdpCoverUrl = new URL(jdpCoverLink);
  const [imageId, childId] = compact(jdpCoverUrl?.pathname?.split('/'));

  if (!imageId || !childId) {
    return processError({
      errorMessage: 'Brand is corrupted',
      language,
      showToast,
      loadingToggle: isCoverSavingVar,
    });
  }

  const readinessState = await checkImageReadiness({
    id: imageId,
    childId,
  });
  if (readinessState?.error) {
    return processError({
      errorMessage: readinessState?.error,
      language,
      showToast,
      loadingToggle: isCoverSavingVar,
    });
  }

  // @ts-ignore Fix UploadImage type
  useStore.getState().setCoverExif(res.exifData);

  const nextBrandState = set(
    cloneDeep(brandStateVar()),
    'assets.cover',
    pick(res, validBrandAssetProps),
  );
  brandStateVar(nextBrandState);
  isCoverSavingVar(false);
};

export const selectExistingCover = (cover: Asset<CoverStrategies>) => {
  debug('selecting existing cover', cover);
  selectedFileVar(null);

  const nextBrandState = set(
    cloneDeep(brandStateVar()),
    'assets.cover',
    pick(cover, validBrandAssetProps),
  );
  brandStateVar(nextBrandState);
};

export const removeCover = () => {
  debug('remove cover');

  const nextBrandState = set(cloneDeep(brandStateVar()), 'assets.cover', null);
  brandStateVar(nextBrandState);
};

export const clearEditorStates = () => {
  debug('Clearing brand editor images and state');

  brandStateVar({
    id: undefined,
    name: '',
    ownerId: undefined,
  });
};

export const setBrandState = (brand: Partial<Brand>) => {
  debug('Setting brand state', brand);
  brandStateVar(brand);
};

export const removeLogo = () => {
  const nextBrandState = set(cloneDeep(brandStateVar()), 'assets.logo', null);

  brandStateVar(nextBrandState);
};

export const pushToToastQueue = (toast: Toast) => {
  const currentQueue = toastQueueVar();
  toastQueueVar([...currentQueue, toast]);
};

export const clearToastQueue = () => {
  toastQueueVar([]);
};

export const setOriginalBrandState = (brand: Partial<Brand>) => {
  debug('Setting original brand state', brand);
  originalBrandStateVar(brand);
};

// TODO: if it grows, we need a dedicated place for it
export const trackBrandSavedEvent = ({
  isCreated,
  brand,
}: {
  isCreated: boolean;
  brand: Brand;
}) => {
  const { accountInfo, userInfo, coverExif } = useStore.getState();

  const trackingEvent = {
    type: 'brandSaved',
    data: {
      channel: 'web',
      view: 'branding-tool',
      ...{
        brand: {
          ...brand,
          ownerid: brand.owner.id,
        },
        isCreated,
        coverMetadata: coverExif,
        accountInfo: {
          ...accountInfo,
          hasImpersonatorUsername: Boolean(accountInfo?.impersonatorUsername),
        },
        userInfo,
      },
    },
  };

  trackingClient.trackGeneral(trackingEvent);
  useStore.getState().setCoverExif(null);
};

export const saveUserInfo = (userInfo: any) => {
  useStore.setState({ userInfo });
};

export const saveAccountInfo = (accountInfo: any) => {
  useStore.setState({ accountInfo });
};
